import { OrganizationProfile } from "@/pages/Private/pages/List/schema/organization-profiles";

import {
	ContactListInfo,
	ContactListPerson,
	ContactProspect,
	ContactProspectOrganization,
} from "../schema/contacts";

/**
 * Turns a Profiles-tab colleague into the preview the lead panel opens on, so clicking a card
 * re-targets the panel immediately and the full detail fetch fills in the rest (PREC-4116).
 *
 * The colleague's `lists` come straight across as list memberships — they carry the real
 * `listPersonId`, which is what the panel resolves its Chat tab and action row from, so the
 * re-targeted panel is usable before the detail lands.
 *
 * Two fields are deliberately not guessed:
 *
 * - `organization` is the **open lead's**, because the Profiles tab only ever lists people at that
 *   same employer. It is passed in rather than rebuilt, so the company card does not flicker.
 * - `scorePersonaCategoryResult` stays `null`. The profile row carries the verdict and its
 *   reasoning but not the per-criterion breakdown behind the badge's hover card, and filling the
 *   rest with placeholders would render an empty breakdown as if it were the lead's. The badge
 *   resolves when the detail arrives, the way every other preview-opened panel does.
 */
export const toProfileProspectPreview = (
	profile: OrganizationProfile,
	organization: ContactProspectOrganization | null
): ContactProspect => {
	// `updatedAt` is required on the membership but the colleagues payload does not carry one, and
	// nothing on the panel reads it — an empty string beats inventing a date.
	const listPersons: ContactListPerson[] = profile.lists.map((list) => ({
		id: list.listPersonId,
		listId: list.listId,
		assignStatus: list.assignStatus,
		updatedAt: "",
	}));
	const lists: ContactListInfo[] = profile.lists.map((list) => ({
		id: list.listId,
		name: list.listName,
	}));

	return {
		id: profile.prospectId,
		name: profile.name,
		firstName: profile.name?.split(" ")[0] ?? null,
		lastName: null,
		email: profile.email,
		emailStatus: profile.emailStatus,
		title: profile.title,
		seniority: null,
		linkedinUrl: profile.linkedinUrl,
		city: null,
		state: null,
		country: null,
		image: profile.image,
		phone: profile.phone,
		phoneStatus: profile.phoneStatus,
		headline: null,
		sentiment: null,
		gender: null,
		genderProbability: null,
		createdAt: null,
		updatedAt: null,
		organization,
		scorePersonaCategoryResult: null,
		listPersons,
		lists,
	};
};
